import React, { useState, useEffect, useRef } from 'react';
import { UserRead, DeviceRead } from '../types/api';
import { apiService } from '../services/api';
import { isPushSupported, isPushSubscribed, initPushNotifications, unsubscribeFromPush } from '../services/pushNotifications';
import { Mail, Phone, Calendar, Shield, Check, AlertTriangle, LogOut, Bell, BellOff, Smartphone, Trash2 } from 'lucide-react';
import { ContactsManager } from './ContactsManager';

interface UserSettingsProps {
  initialUser?: UserRead | null;
}

type PhoneVerificationState = 'idle' | 'entering-phone' | 'warning' | 'entering-code' | 'success';

export const UserSettings: React.FC<UserSettingsProps> = ({ initialUser }) => {
  const [user, setUser] = useState<UserRead | null>(initialUser || null);
  const [loading, setLoading] = useState(!initialUser);
  const [error, setError] = useState<string | null>(null);
  
  // Phone verification state
  const [phoneState, setPhoneState] = useState<PhoneVerificationState>('idle');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSuccess, setPhoneSuccess] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes
  
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Push notification state
  const [pushSupported] = useState(isPushSupported());
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushDevices, setPushDevices] = useState<DeviceRead[]>([]);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (!initialUser) {
      loadUserDetails();
    }
  }, [initialUser]);

  useEffect(() => {
    if (pushSupported) {
      loadPushState();
    }
  }, [pushSupported]);

  const loadUserDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Countdown timer for verification code
  useEffect(() => {
    if (phoneState === 'entering-code' && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [phoneState, timeRemaining]);

  // Format countdown timer
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Validate phone number format
  const isValidPhoneNumber = (phone: string): boolean => {
    return /^\+[1-9]\d{1,14}$/.test(phone);
  };

  // Handle phone number input
  const handlePhoneNumberChange = (value: string) => {
    // Allow only digits, plus sign, and basic formatting
    const cleaned = value.replace(/[^\d+]/g, '');
    setPhoneNumber(cleaned);
  };

  // Request verification code
  const handleRequestVerification = async () => {
    setPhoneError(null);
    
    if (!isValidPhoneNumber(phoneNumber)) {
      setPhoneError('Invalid phone format. Use international format: +1234567890');
      return;
    }

    setPhoneLoading(true);
    try {
      const response = await apiService.requestPhoneVerification(phoneNumber);
      
      if (response.phone_already_used) {
        setPhoneError('This phone number is already in use.');
        setPhoneState('warning');
      } else {
        setPhoneState('entering-code');
        setTimeRemaining(300);
      }
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setPhoneLoading(false);
    }
  };

  // Continue after warning
  const handleContinueAfterWarning = async () => {
    setPhoneState('entering-code');
    setTimeRemaining(300);
  };

  // Handle code input
  const handleCodeInput = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/[^\d]/g, '').slice(0, 1);
    
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (digit && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (digit && index === 5 && newCode.every(d => d !== '')) {
      handleVerifyCode(newCode.join(''));
    }
  };

  // Handle backspace in code input
  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Verify code
  const handleVerifyCode = async (code?: string) => {
    const codeToVerify = code || verificationCode.join('');
    
    if (codeToVerify.length !== 6) {
      setPhoneError('Please enter all 6 digits');
      return;
    }

    setPhoneLoading(true);
    setPhoneError(null);
    
    try {
      const response = await apiService.verifyPhoneCode(phoneNumber, codeToVerify);
      
      // Refresh user data
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      
      // Show success
      setPhoneSuccess(response.message);
      setPhoneState('success');
      
      // Reset after 3 seconds
      setTimeout(() => {
        resetPhoneState();
      }, 3000);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Verification failed');
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setPhoneLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    setPhoneError(null);
    setVerificationCode(['', '', '', '', '', '']);
    
    setPhoneLoading(true);
    try {
      await apiService.requestPhoneVerification(phoneNumber);
      setTimeRemaining(300);
      setPhoneSuccess('New code sent!');
      setTimeout(() => setPhoneSuccess(null), 3000);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setPhoneLoading(false);
    }
  };

  // Detach phone
  const handleDetachPhone = async () => {
    if (!window.confirm('Are you sure you want to remove your phone number?')) {
      return;
    }

    setPhoneLoading(true);
    setPhoneError(null);
    
    try {
      const response = await apiService.detachPhone();
      
      // Refresh user data
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      
      setPhoneSuccess(response.message);
      setTimeout(() => setPhoneSuccess(null), 3000);
    } catch (err) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to remove phone number');
    } finally {
      setPhoneLoading(false);
    }
  };

  // Reset phone verification state
  const resetPhoneState = () => {
    setPhoneState('idle');
    setPhoneNumber('');
    setVerificationCode(['', '', '', '', '', '']);
    setPhoneError(null);
    setPhoneSuccess(null);
    setTimeRemaining(300);
  };

  const loadPushState = async () => {
    try {
      const subscribed = await isPushSubscribed();
      setPushEnabled(subscribed);
      if (subscribed) {
        const devices = await apiService.listNotificationDevices();
        setPushDevices(devices);
      }
    } catch {
      // non-critical
    }
  };

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        setPushDevices([]);
      } else {
        await initPushNotifications();
        setPushEnabled(true);
        const devices = await apiService.listNotificationDevices();
        setPushDevices(devices);
      }
    } catch (err) {
      console.error('Push toggle failed:', err);
    } finally {
      setPushLoading(false);
    }
  };

  const handleRemoveAllDevices = async () => {
    if (!window.confirm('Remove all registered devices? You will stop receiving notifications on all browsers.')) return;
    setPushLoading(true);
    try {
      await apiService.removeAllNotificationDevices();
      await unsubscribeFromPush();
      setPushEnabled(false);
      setPushDevices([]);
    } catch (err) {
      console.error('Remove devices failed:', err);
    } finally {
      setPushLoading(false);
    }
  };

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && phoneState !== 'idle') {
        resetPhoneState();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [phoneState]);

  // Auto-hide success messages
  useEffect(() => {
    if (phoneSuccess) {
      const timer = setTimeout(() => setPhoneSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [phoneSuccess]);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 pt-6 pb-24">
      <h2 className="text-xl font-semibold mb-6" style={{ color: '#132B64' }}>Account Settings</h2>

      <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#132B64' }}></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
              <button
                onClick={loadUserDetails}
                className="mt-3 text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Try again
              </button>
            </div>
          ) : user ? (
            <div className="space-y-6">
              {/* Profile Section */}
              <div className="rounded-lg p-6 text-center" style={{ backgroundColor: '#ebf6e4', border: '1px solid #cbe0be' }}>
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 bg-white" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #cbe0be' }}>
                  <img src="/icons/helmet_434059.png" alt="Profile" className="w-14 h-14" style={{ objectFit: 'contain', objectPosition: 'center' }} />
                </div>
                <h3 className="text-xl font-semibold mb-1" style={{ color: '#132B64' }}>
                  {user.username}
                </h3>
                {user.is_superuser && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <Shield size={12} className="mr-1" />
                    Admin
                  </span>
                )}
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Contact Information
                </h4>

                {/* Email */}
                <div className="rounded-lg p-4" style={{ backgroundColor: '#ebf6e4', border: '1px solid #cbe0be' }}>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Mail size={18} style={{ color: '#233454' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs mb-1" style={{ color: '#233454', opacity: 0.7 }}>Email Address</p>
                      <p className="text-sm font-medium break-all" style={{ color: '#233454' }}>
                        {user.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Phone Number Management */}
                <div className="rounded-lg p-4" style={{ backgroundColor: '#ebf6e4', border: '1px solid #cbe0be' }}>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Phone size={18} style={{ color: '#233454' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs mb-2" style={{ color: '#233454', opacity: 0.7 }}>Phone Number</p>
                      
                      {/* Success Message */}
                      {phoneSuccess && (
                        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded flex items-center text-green-800 text-sm">
                          <Check size={16} className="mr-2 flex-shrink-0" />
                          {phoneSuccess}
                        </div>
                      )}

                      {/* Error Message */}
                      {phoneError && (
                        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
                          {phoneError}
                        </div>
                      )}

                      {/* State: User has verified phone */}
                      {phoneState === 'idle' && user.phone_number && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900">
                              {user.phone_number}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Check size={12} className="mr-1" />
                              Verified
                            </span>
                          </div>
                          <button
                            onClick={handleDetachPhone}
                            disabled={phoneLoading}
                            className="w-full px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {phoneLoading ? 'Removing...' : 'Remove Phone Number'}
                          </button>
                        </div>
                      )}

                      {/* State: User has no phone */}
                      {phoneState === 'idle' && !user.phone_number && (
                        <button
                          onClick={() => setPhoneState('entering-phone')}
                          className="w-full px-3 py-2 text-sm rounded-md transition-colors"
                          style={{ color: '#132B64', borderWidth: '1px', borderStyle: 'solid', borderColor: '#132B64' }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e6ecfa')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                        >
                          + Add Phone Number
                        </button>
                      )}

                      {/* State: Entering phone number */}
                      {phoneState === 'entering-phone' && (
                        <div className="space-y-3">
                          <div>
                            <input
                              type="tel"
                              value={phoneNumber}
                              onChange={(e) => handlePhoneNumberChange(e.target.value)}
                              placeholder="+1234567890"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none"
                              onFocus={(e) => { e.target.style.borderColor = '#132B64'; e.target.style.boxShadow = '0 0 0 1px #132B64'; }}
                              onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                              autoFocus
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Format: +[country code][number]
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={resetPhoneState}
                              disabled={phoneLoading}
                              className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleRequestVerification}
                              disabled={phoneLoading || !isValidPhoneNumber(phoneNumber)}
                              className="flex-1 px-3 py-2 text-sm text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ backgroundColor: '#132B64' }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0f2350')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#132B64')}
                            >
                              {phoneLoading ? 'Sending...' : 'Send Code'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* State: Warning if phone is used by another account */}
                      {phoneState === 'warning' && (
                        <div className="space-y-3">
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                            <div className="flex items-start">
                              <AlertTriangle size={18} className="text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                              <div className="text-sm text-amber-800">
                                <p className="font-medium mb-1">Phone Number In Use</p>
                                <p>This number is currently associated with another account. Verifying will remove it from that account.</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={resetPhoneState}
                              disabled={phoneLoading}
                              className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleContinueAfterWarning}
                              disabled={phoneLoading}
                              className="flex-1 px-3 py-2 text-sm text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
                            >
                              Continue
                            </button>
                          </div>
                        </div>
                      )}

                      {/* State: Entering verification code */}
                      {phoneState === 'entering-code' && (
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-gray-700 mb-1">Verification Code Sent</p>
                            <p className="text-xs text-gray-500 mb-3">
                              Enter the 6-digit code sent to {phoneNumber}
                            </p>
                            
                            {/* 6-digit code input */}
                            <div className="flex justify-between mb-3 gap-1 sm:gap-2">
                              {verificationCode.map((digit, index) => (
                                <input
                                  key={index}
                                  ref={(el) => { codeInputRefs.current[index] = el; }}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={1}
                                  value={digit}
                                  onChange={(e) => handleCodeInput(index, e.target.value)}
                                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                  className="w-10 h-10 sm:w-12 sm:h-12 text-center text-base sm:text-lg font-semibold border border-gray-300 rounded-md focus:outline-none"
                                  onFocus={(e) => { e.target.style.borderColor = '#132B64'; e.target.style.boxShadow = '0 0 0 1px #132B64'; }}
                                  onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                                />
                              ))}
                            </div>

                            {/* Timer and resend */}
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>
                                {timeRemaining > 0 ? `Code expires in ${formatTime(timeRemaining)}` : 'Code expired'}
                              </span>
                              <button
                                onClick={handleResendCode}
                                disabled={phoneLoading}
                                className="disabled:opacity-50"
                                style={{ color: '#132B64' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#0f2350')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#132B64')}
                              >
                                Resend Code
                              </button>
                            </div>
                          </div>

                          <div className="flex space-x-2">
                            <button
                              onClick={resetPhoneState}
                              disabled={phoneLoading}
                              className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleVerifyCode()}
                              disabled={phoneLoading || verificationCode.some(d => d === '')}
                              className="flex-1 px-3 py-2 text-sm text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ backgroundColor: '#132B64' }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0f2350')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#132B64')}
                            >
                              {phoneLoading ? 'Verifying...' : 'Verify'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* State: Success */}
                      {phoneState === 'success' && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-md text-center">
                          <Check size={48} className="text-green-600 mx-auto mb-2" />
                          <p className="text-sm font-medium text-green-800">
                            Phone number verified successfully!
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contacts Manager */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Saved Contacts
                </h4>
                <p className="text-xs text-gray-500">
                  Maintain contacts for trainers, vets, and buyers, then attach them directly to horse notes.
                </p>
                <ContactsManager />
              </div>

              {/* Push Notifications */}
              {pushSupported && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                    Notifications
                  </h4>
                  <div className="rounded-lg p-4" style={{ backgroundColor: '#ebf6e4', border: '1px solid #cbe0be' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {pushEnabled ? (
                          <Bell size={18} style={{ color: '#233454' }} />
                        ) : (
                          <BellOff size={18} className="text-gray-400" />
                        )}
                        <div>
                          <p className="text-sm font-medium" style={{ color: '#233454' }}>
                            Push Notifications
                          </p>
                          <p className="text-xs text-gray-500">
                            {pushEnabled ? 'Enabled' : 'Disabled'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleTogglePush}
                        disabled={pushLoading}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                          pushEnabled ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={pushEnabled}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            pushEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {pushEnabled && pushDevices.length > 0 && (
                      <div className="border-t pt-3" style={{ borderColor: '#cbe0be' }}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Smartphone size={14} className="text-gray-500" />
                            <span className="text-xs text-gray-600">
                              {pushDevices.length} registered device{pushDevices.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <button
                            onClick={handleRemoveAllDevices}
                            disabled={pushLoading}
                            className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            <Trash2 size={12} />
                            <span>Remove all</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Account Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wider">
                  Account Details
                </h4>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                  {/* Member Since */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} className="text-gray-400" />
                      <span className="text-xs text-gray-500">Member Since</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDate(user.created_at)}
                    </span>
                  </div>

                  {/* Sign Out */}
                  <div className="pt-3 border-t border-gray-200">
                    <button
                      onClick={() => { apiService.logout(); window.location.reload(); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                    >
                      <LogOut size={15} />
                      Sign out
                    </button>
                  </div>

                  {/* Account Status */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className="text-xs text-gray-500">Status</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No user data available</p>
            </div>
          )}
      </div>
    </div>
  );
};
